import { type ErrorInfo, StrictMode } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'

import { ApiGatewayProvider } from '@dhl/api-gateway-provider'
import { dataLayerPublish } from '@dhl/datalayer'
import { MapProvider } from '@dhl/google-maps'
import { SnackbarProvider } from '@dhl-ui/components'
import { VersionProvider } from '@dhl/version-provider'
import { I18nProvider } from '@dhl/i18n'
import { CacheProvider, ThemeProvider } from '@dhl-ui/connector'
import { useMobileViewportHeight } from '@dhl/viewport-height'
import { useSetupLocalXsrfRequestInterceptor } from '@dhl/receiver-axios'
import { initDataLayer } from '@dhl/third-party'

import { LoggedInFilter } from '~/profile/LoggedInFilter'
import { Routes } from '~/Routes'
import { getConfig } from '~/config'
import { StartUp } from '~/StartUp'
import { TrackingKeyProvider, TrackingPostalCodeProvider } from '~/useTrackingKey'
import { queryClient } from './query-client'
import { OnboardingShellPage } from './components/OnboardingShellPage'
import { ErrorBoundary } from './components/ErrorBoundary'

initDataLayer()

const { apiGatewayHost, basePath, cspNonce, version, maps } = getConfig()

const baseNameWithoutTrailingSlash = basePath.slice(0, -1)

export const App = () => {
  useMobileViewportHeight()
  useSetupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    dataLayerPublish('ERROR', { id: 'error-boundary', error, errorInfo })
  }

  return (
    <CacheProvider nonce={cspNonce}>
      <ThemeProvider options={{ disablePtr: true }}>
        <ApiGatewayProvider host={apiGatewayHost}>
          <ErrorBoundary PageComponent={OnboardingShellPage} onError={handleError}>
            <QueryClientProvider client={queryClient}>
              <MapProvider apiKey={maps.apiKey} nonce={cspNonce}>
                <SnackbarProvider>
                  <I18nProvider>
                    <BrowserRouter
                      basename={baseNameWithoutTrailingSlash}
                      future={{
                        v7_relativeSplatPath: true,
                        v7_startTransition: true,
                      }}
                    >
                      <VersionProvider url="/home/version.json" version={version}>
                        <LoggedInFilter>
                          <StartUp>
                            <StrictMode>
                              <TrackingKeyProvider>
                                <TrackingPostalCodeProvider>
                                  <Routes />
                                </TrackingPostalCodeProvider>
                              </TrackingKeyProvider>
                            </StrictMode>
                          </StartUp>
                        </LoggedInFilter>
                      </VersionProvider>
                    </BrowserRouter>
                  </I18nProvider>
                </SnackbarProvider>
              </MapProvider>
            </QueryClientProvider>
          </ErrorBoundary>
        </ApiGatewayProvider>
      </ThemeProvider>
    </CacheProvider>
  )
}
